/** @file Mix preview section: from/to animations, crossfade seconds, preview/stop, save as the default. */
import type { Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { useMemo } from 'react';
import { useStore } from 'zustand';
import type { EditorDeps } from '../../playback/editorDeps';
import { mixPreviewStore } from '../../playback/mixPreviewStore';
import { Button } from '../../ui-kit/Button';
import { NumberField } from '../../ui-kit/NumberField';
import { Select } from '../../ui-kit/Select';
import type { AnimationRowData } from './animationRows';
import { mixPreviewActions } from './mixPreviewActions';

/**
 * The viewport shows the preview while it runs (currentPoseAt); any playhead move stops it. Only the pair, the typed
 * seconds and whether a run exists are selected: the per-frame `tick` never re-renders this section (spec §9).
 */
export function MixPreview({ doc, rows, deps, t }: { doc: Doc; rows: readonly AnimationRowData[]; deps: EditorDeps; t: Translate }) {
  const from = useStore(mixPreviewStore, (s) => s.from);
  const to = useStore(mixPreviewStore, (s) => s.to);
  const seconds = useStore(mixPreviewStore, (s) => s.seconds);
  const running = useStore(mixPreviewStore, (s) => s.run !== null);
  const data = deps.data();
  const shown = useMemo(() => mixPreviewActions.seconds(doc, data, { from, to, seconds }), [doc, data, from, to, seconds]);
  const options = [{ value: '', label: '—' }, ...rows.map((r) => ({ value: r.id, label: r.name }))];
  const pick = (side: 'from' | 'to') => (value: string): void => { mixPreviewStore.setState({ [side]: value === '' ? null : value }); };
  return (
    <section className="animations-mix" aria-label={t('panels.animations.mix.title')}>
      <h3>{t('panels.animations.mix.title')}</h3>
      <Select label={t('panels.animations.mix.from')} value={from ?? ''} options={options} onChange={pick('from')} />
      <Select label={t('panels.animations.mix.to')} value={to ?? ''} options={options} onChange={pick('to')} />
      <NumberField label={t('panels.animations.mix.seconds')} value={shown} min={0} step={0.05} precision={2} onCommit={(seconds) => { mixPreviewStore.setState({ seconds }); }} />
      <Button label={t(running ? 'panels.animations.mix.stop' : 'panels.animations.mix.play')} primary onClick={() => { if (running) mixPreviewActions.stop(); else mixPreviewActions.start(doc, deps.data()); }} />
      <Button label={t('panels.animations.mix.save')} disabled={from === null || to === null} onClick={() => { deps.dispatch(mixPreviewActions.saveCalls(doc, deps.data())); }} />
    </section>
  );
}
